import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores'
import { useChoresStore } from '@/stores/chores'
import { logNavigation, warnNavigation } from '@/utils/navigation-debug'

export interface NavItem {
  id: string
  path: string
  icon: string
  label: string
  badge?: number
  ariaLabel?: string
}

// Navigation item constants - defines all possible nav items
const NAV_ITEMS = {
  dashboard: { id: 'dashboard', path: '/dashboard', icon: 'mdi-home', label: 'Dashboard' },
  chores: { id: 'chores', path: '/chores', icon: 'mdi-broom', label: 'Chores' },
  taskLibrary: { id: 'task-library', path: '/tasks', icon: 'mdi-clipboard-list', label: 'Task library' },
  rewards: { id: 'rewards', path: '/rewards', icon: 'mdi-gift', label: 'Rewards' },
  family: { id: 'family', path: '/family', icon: 'mdi-account-group', label: 'Family' },
  review: { id: 'review', path: '/review', icon: 'mdi-clipboard-check', label: 'Review' },
  settings: { id: 'settings', path: '/settings', icon: 'mdi-cog', label: 'Settings' },
  admin: { id: 'admin', path: '/admin', icon: 'mdi-shield-account', label: 'Admin' },
} as const satisfies Record<string, NavItem>

/**
 * Provides role-based navigation items and routing utilities
 *
 * Returns different navigation items based on the current user's role:
 * - Parent: Dashboard, Review, Chores, Rewards, Family, Task library, Settings (+ Admin)
 *   Bottom navigation: Home, Review, Chores, Rewards, Family
 * - Kid: Today, My chores, Rewards, Me in both navigation lists
 * - Guest or auth loading: Empty arrays
 */
export function useNavigation() {
  const authStore = useAuthStore()
  const choresStore = useChoresStore()
  const route = useRoute()
  const router = useRouter()

  /**
   * Get navigation items based on user role
   */
  const navItems = computed((): NavItem[] => {
    const role = authStore.currentAccountRole

    // Suppress nav entirely while auth state is resolving.
    if (!role || authStore.isLoading) {
      return []
    }

    let items: NavItem[]

    // Minor role - limited navigation
    if (role === 'minor') {
      items = [
        { ...NAV_ITEMS.dashboard, label: 'Today' },
        { ...NAV_ITEMS.chores, label: 'My chores' },
        NAV_ITEMS.rewards,
        { ...NAV_ITEMS.settings, label: 'Me' },
      ]
    } else if (role === 'adult') {
      items = [
        NAV_ITEMS.dashboard,
        NAV_ITEMS.review,
        NAV_ITEMS.chores,
        NAV_ITEMS.rewards,
        NAV_ITEMS.family,
        NAV_ITEMS.taskLibrary,
        NAV_ITEMS.settings,
      ]
      if (authStore.isAdmin) {
        items.push(NAV_ITEMS.admin)
      }
    } else {
      return []
    }

    return items.map((item) => {
      if (item.id === 'review') {
        const count = choresStore.pendingReviewCount
        return {
          ...item,
          badge: count > 0 ? count : undefined,
          ariaLabel: count > 0 ? `Review, ${count} waiting` : 'Review',
        }
      }
      return { ...item, ariaLabel: item.label }
    })
  })

  const bottomNavItems = computed((): NavItem[] => {
    const items = authStore.currentAccountRole === 'adult'
      ? navItems.value.slice(0, 5)
      : navItems.value

    return items.map((item) => {
      if (authStore.currentAccountRole === 'adult' && item.id === 'dashboard') {
        return { ...item, label: 'Home', ariaLabel: 'Home' }
      }
      return { ...item }
    })
  })

  /**
   * Check if the given path is the currently active route
   */
  const isActive = (path: string): boolean => {
    return route.path === path || route.path.startsWith(path + '/')
  }

  /**
   * Navigate to the specified path
   */
  const navigateTo = async (path: string): Promise<void> => {
    const currentPath = route.path
    const role = authStore.currentAccountRole

    // Log navigation attempt
    logNavigation({
      prefix: '[Navigation]',
      event: 'navigateTo:start',
      from: currentPath,
      to: path,
      metadata: { role },
    })

    // Warn if navigating to same path (potential "reload" cause)
    if (currentPath === path) {
      warnNavigation({
        prefix: '[Navigation]',
        event: 'navigateTo:samePath',
        from: currentPath,
        to: path,
        reason: 'navigation to same path - may cause page reload instead of navigation',
        metadata: { role },
      })
    }

    try {
      await router.push(path)
      logNavigation({
        prefix: '[Navigation]',
        event: 'navigateTo:success',
        from: currentPath,
        to: path,
      })
    } catch (error) {
      warnNavigation({
        prefix: '[Navigation]',
        event: 'navigateTo:error',
        from: currentPath,
        to: path,
        reason: error instanceof Error ? error.message : 'unknown error',
        metadata: { error: error instanceof Error ? error.name : 'Unknown' },
      })
      throw error
    }
  }

  return {
    navItems,
    bottomNavItems,
    isActive,
    navigateTo,
  }
}
